import { useEffect, useId, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { cancelWorkflowRun, fetchWorkflowRun, fetchWorkflowRuns } from "../lib/api";
import { useCan } from "../lib/session";
import type {
  ChipTone,
  WorkflowNode,
  WorkflowRunDetailResponse,
  WorkflowRunStatus,
  WorkflowRunStep,
  WorkflowRunSummary,
  WorkflowSummary,
} from "../lib/types";
import { StatusBadge, Surface, SurfaceHeader } from "./PortalPrimitives";

const RUN_STATUSES: WorkflowRunStatus[] = [
  "queued",
  "running",
  "waiting_on_approval",
  "succeeded",
  "failed",
  "cancelled",
  "expired",
];

const ACTIVE_STATUSES = new Set(["queued", "running", "waiting_on_approval"]);

export function runChipTone(status: string): ChipTone {
  switch (status) {
    case "succeeded":
      return "green";
    case "running":
    case "queued":
      return "blue";
    case "waiting_on_approval":
    case "expired":
      return "amber";
    case "failed":
      return "red";
    default:
      return "gray";
  }
}

function statusLabel(status: string): string {
  if (status === "waiting_on_approval") return "Needs approval";
  return status.replaceAll("_", " ");
}

function formatWhen(value: string | null): string {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Date unavailable";
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
}

function duration(start: string | null, end: string | null): string {
  if (!start) return "—";
  const from = Date.parse(start);
  const to = end ? Date.parse(end) : Date.now();
  if (Number.isNaN(from) || Number.isNaN(to) || to < from) return "—";
  const seconds = Math.round((to - from) / 1000);
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
  return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m`;
}

function modelCost(run: { cost_estimate_usd: number | null }): string {
  return run.cost_estimate_usd == null ? "—" : `$${run.cost_estimate_usd.toFixed(4)}`;
}

export function chainFromSnapshot(snapshot: {
  nodes?: WorkflowNode[];
  edges?: Array<[string, string]>;
}): WorkflowNode[] {
  const nodes = snapshot?.nodes ?? [];
  if (nodes.length === 0) return [];
  const byId = new Map(nodes.map((node) => [node.id, node]));
  const next = new Map((snapshot.edges ?? []).map(([source, target]) => [source, target]));
  const chain: WorkflowNode[] = [nodes[0]];
  const seen = new Set([nodes[0].id]);
  let cursor: string | undefined = nodes[0].id;
  while (cursor && next.has(cursor)) {
    cursor = next.get(cursor);
    if (!cursor || seen.has(cursor)) break;
    seen.add(cursor);
    const node = byId.get(cursor);
    if (!node) break;
    chain.push(node);
  }
  return chain;
}

function latestStepPerNode(steps: WorkflowRunStep[]): Map<string, WorkflowRunStep> {
  const latest = new Map<string, WorkflowRunStep>();
  for (const step of steps) {
    const prior = latest.get(step.node_id);
    if (!prior || step.attempt >= prior.attempt) latest.set(step.node_id, step);
  }
  return latest;
}

function nodeStatus(
  node: WorkflowNode,
  run: WorkflowRunDetailResponse["run"],
  latest: Map<string, WorkflowRunStep>,
): string {
  if (node.type.startsWith("trigger.")) return "succeeded";
  const step = latest.get(node.id);
  if (step) return step.status;
  if (run.status === "running" && run.current_step_node_id === node.id) return "running";
  return "pending";
}

function nodeLabel(type: string): string {
  if (type.startsWith("trigger.")) return "Trigger";
  if (type === "agent.tool") return "Tool step";
  if (type === "llm") return "Analysis";
  if (type === "control.approval") return "Approval";
  if (type === "notify.email") return "Notification";
  return "Workflow step";
}

function StepSummary({ steps }: { steps: WorkflowRunStep[] }) {
  if (steps.length === 0) return <div className="agents-runs-empty">No steps have started yet.</div>;
  return (
    <ol className="agents-step-list">
      {steps.map((step) => (
        <li key={step.workflow_run_step_id}>
          <div>
            <strong>{nodeLabel(step.node_type)}</strong>
            <span>
              {formatWhen(step.started_at)} · {duration(step.started_at, step.completed_at)}
              {step.attempt > 1 ? ` · attempt ${step.attempt}` : ""}
            </span>
          </div>
          <StatusBadge tone={runChipTone(step.status)}>{statusLabel(step.status)}</StatusBadge>
        </li>
      ))}
    </ol>
  );
}

export function RunDetail({ runId, onClose }: { runId: string; onClose: () => void }) {
  const canRun = useCan("workflows.run");
  const queryClient = useQueryClient();
  const [confirmCancel, setConfirmCancel] = useState(false);
  const confirmationDescriptionId = useId();
  const cancelTriggerRef = useRef<HTMLButtonElement>(null);
  const confirmationActionRef = useRef<HTMLButtonElement>(null);
  const cancelPendingRef = useRef(false);
  const detail = useQuery({
    queryKey: ["workflow-run", runId],
    queryFn: () => fetchWorkflowRun(runId),
    retry: false,
    refetchInterval: (query) =>
      query.state.data && ACTIVE_STATUSES.has(query.state.data.run.status) ? 5000 : false,
  });
  const cancel = useMutation({
    mutationFn: () => cancelWorkflowRun(runId),
    onSuccess: () => {
      setConfirmCancel(false);
      void queryClient.invalidateQueries({ queryKey: ["workflow-run", runId] });
      void queryClient.invalidateQueries({ queryKey: ["workflow-runs"] });
    },
  });
  cancelPendingRef.current = cancel.isPending;

  useEffect(() => {
    if (!confirmCancel) return;
    const returnTarget = cancelTriggerRef.current;
    confirmationActionRef.current?.focus();
    const handleKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key !== "Escape" || cancelPendingRef.current) return;
      event.preventDefault();
      setConfirmCancel(false);
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      window.setTimeout(() => returnTarget?.focus(), 0);
    };
  }, [confirmCancel]);

  if (detail.isLoading) {
    return <div className="agents-state" role="status">Loading run details…</div>;
  }
  if (detail.isError || !detail.data) {
    return (
      <div className="agents-state agents-state--error" role="alert">
        <strong>Run details could not be loaded.</strong>
        <button className="btn" type="button" onClick={() => void detail.refetch()}>
          Try again
        </button>
        <button className="btn" type="button" onClick={onClose}>Close</button>
      </div>
    );
  }

  const { run, steps, approval } = detail.data;
  const chain = chainFromSnapshot(
    run.canonical_snapshot as { nodes?: WorkflowNode[]; edges?: Array<[string, string]> },
  );
  const latest = latestStepPerNode(steps);
  const cancellable = run.status === "queued" || run.status === "waiting_on_approval";

  return (
    <Surface className="agents-run-detail" aria-labelledby="agents-run-detail-title">
      <SurfaceHeader
        title={run.definition_name.replace(/^library:\s*/, "")}
        titleId="agents-run-detail-title"
        detail={`${run.mode === "dry_run" ? "Test run" : "Live run"} · started ${formatWhen(run.started_at ?? run.created_at)}`}
        action={
          <div className="agents-run-detail-actions">
            {cancellable && canRun ? (
              <button
                ref={cancelTriggerRef}
                className="btn"
                type="button"
                disabled={confirmCancel}
                onClick={() => setConfirmCancel(true)}
              >
                Cancel run
              </button>
            ) : null}
            <button className="btn" type="button" onClick={onClose}>Close</button>
          </div>
        }
      />

      <div className="agents-run-outcome">
        <StatusBadge tone={runChipTone(run.status)}>{statusLabel(run.status)}</StatusBadge>
        <div>
          <strong>
            {run.status === "succeeded"
              ? "Run completed"
              : run.status === "failed"
                ? "Run needs attention"
                : run.status === "waiting_on_approval"
                  ? "A decision is required"
                  : `Run is ${statusLabel(run.status)}`}
          </strong>
          <span>
            {run.steps_completed} of {run.steps_total} steps · {duration(run.started_at, run.completed_at)} · estimated model cost {modelCost(run)}
          </span>
        </div>
      </div>

      {run.status === "failed" ? (
        <div className="agents-run-warning" role="alert">
          This run failed. Technical diagnostics remain in Operations; no raw tool payloads are shown here.
        </div>
      ) : null}

      {approval?.status === "pending" ? (
        <div className="agents-approval-callout">
          <div>
            <strong>Approval needed</strong>
            <span>{approval.summary || "Review the proposed next action."}</span>
          </div>
          <Link className="btn primary" to={approval.deep_link.replace(/^\/app/, "")}>
            Review decision
          </Link>
        </div>
      ) : null}

      {confirmCancel ? (
        <div
          className="agents-confirm agents-run-cancel"
          role="alertdialog"
          aria-label="Confirm run cancellation"
          aria-describedby={confirmationDescriptionId}
        >
          <p id={confirmationDescriptionId}>Cancelling stops this queued or approval-waiting run. Completed steps remain in its audit history.</p>
          <div>
            <button
              ref={confirmationActionRef}
              className="btn primary"
              type="button"
              disabled={cancel.isPending}
              onClick={() => cancel.mutate()}
            >
              {cancel.isPending ? "Cancelling…" : "Confirm cancellation"}
            </button>
            <button
              className="btn"
              type="button"
              disabled={cancel.isPending}
              onClick={() => setConfirmCancel(false)}
            >
              Keep run
            </button>
          </div>
          {cancel.isError ? <span role="alert">The run could not be cancelled.</span> : null}
        </div>
      ) : null}

      <div className="agents-run-section">
        <h3>Progress</h3>
        {chain.length > 0 ? (
          <ol className="agents-run-chain" aria-label="Workflow progress">
            {chain.map((node) => {
              const status = nodeStatus(node, run, latest);
              return (
                <li key={node.id}>
                  <span aria-hidden="true" />
                  <div>
                    <strong>{nodeLabel(node.type)}</strong>
                    <StatusBadge tone={runChipTone(status)}>{statusLabel(status)}</StatusBadge>
                  </div>
                </li>
              );
            })}
          </ol>
        ) : (
          <div className="agents-runs-empty">No execution stages are available.</div>
        )}
      </div>

      <div className="agents-run-section">
        <h3>Step history</h3>
        <StepSummary steps={steps} />
      </div>
    </Surface>
  );
}

export default function WorkflowRuns({
  definitions,
  statusFilter,
  definitionFilter,
  onFilterChange,
  selectedRunId,
  onSelectRun,
}: {
  definitions: WorkflowSummary[];
  statusFilter: string;
  definitionFilter: string;
  onFilterChange: (status: string, definitionId: string) => void;
  selectedRunId: string | null;
  onSelectRun: (runId: string | null) => void;
}) {
  const runsQuery = useInfiniteQuery({
    queryKey: ["workflow-runs", statusFilter, definitionFilter],
    queryFn: ({ pageParam }) =>
      fetchWorkflowRuns({
        status: statusFilter || undefined,
        definitionId: definitionFilter || undefined,
        cursor: pageParam,
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.next_cursor,
    retry: false,
    refetchInterval: (query) => {
      const runs = query.state.data?.pages.flatMap((page) => page.runs) ?? [];
      return runs.some((run) => ACTIVE_STATUSES.has(run.status)) ? 10000 : false;
    },
  });
  const runs: WorkflowRunSummary[] = useMemo(
    () => runsQuery.data?.pages.flatMap((page) => page.runs) ?? [],
    [runsQuery.data],
  );

  return (
    <>
      <Surface className="agents-run-filters" aria-labelledby="agents-run-filters-title">
        <SurfaceHeader
          title="Run history"
          titleId="agents-run-filters-title"
          detail="Live and test executions are kept separate in every row."
          action={
            <button
              className="btn"
              type="button"
              disabled={runsQuery.isFetching}
              onClick={() => void runsQuery.refetch()}
            >
              {runsQuery.isFetching && !runsQuery.isFetchingNextPage ? "Refreshing…" : "Refresh"}
            </button>
          }
        />
        <form className="agents-run-filter-form" aria-label="Run filters">
          <label>
            Status
            <select
              value={statusFilter}
              onChange={(event) => onFilterChange(event.target.value, definitionFilter)}
            >
              <option value="">All statuses</option>
              {RUN_STATUSES.map((status) => (
                <option key={status} value={status}>{statusLabel(status)}</option>
              ))}
            </select>
          </label>
          <label>
            Definition version
            <select
              value={definitionFilter}
              onChange={(event) => onFilterChange(statusFilter, event.target.value)}
            >
              <option value="">All definition versions</option>
              {definitions.map((definition) => (
                <option key={definition.workflow_id} value={definition.workflow_id}>
                  {definition.name.replace(/^library:\s*/, "")} · v{definition.version} · {definition.status}
                </option>
              ))}
            </select>
          </label>
        </form>
      </Surface>

      {selectedRunId ? <RunDetail runId={selectedRunId} onClose={() => onSelectRun(null)} /> : null}

      <Surface className="agents-run-list" aria-labelledby="agents-run-list-title">
        <SurfaceHeader title="Executions" titleId="agents-run-list-title" count={runs.length} />
        {runsQuery.isRefetchError && runs.length > 0 ? (
          <div className="agents-refresh-warning agents-run-refresh-warning" role="alert">
            Refresh failed. Previously loaded run history is still shown.
          </div>
        ) : null}
        {runsQuery.isLoading ? <div className="agents-state" role="status">Loading run history…</div> : null}
        {runsQuery.isError && runs.length === 0 ? (
          <div className="agents-state agents-state--error" role="alert">
            <strong>Run history could not be loaded.</strong>
            <button className="btn" type="button" onClick={() => void runsQuery.refetch()}>Try again</button>
          </div>
        ) : null}
        {runs.length === 0 && !runsQuery.isLoading && !runsQuery.isError ? (
          <div className="agents-runs-empty">No runs match these filters yet.</div>
        ) : null}
        {runs.length > 0 ? (
          <div className="agents-run-table-wrap">
            <table className="agents-run-table">
              <caption className="ops-visually-hidden">Workflow execution history</caption>
              <thead>
                <tr>
                  <th scope="col">Automation</th>
                  <th scope="col">Mode</th>
                  <th scope="col">Started</th>
                  <th scope="col">Duration</th>
                  <th scope="col">Progress</th>
                  <th scope="col">Est. model cost</th>
                  <th scope="col">Status</th>
                  <th scope="col"><span className="ops-visually-hidden">Actions</span></th>
                </tr>
              </thead>
              <tbody>
                {runs.map((run) => (
                  <tr key={run.workflow_run_id} className={run.workflow_run_id === selectedRunId ? "is-selected" : ""}>
                    <td data-label="Automation"><strong>{run.definition_name.replace(/^library:\s*/, "")}</strong></td>
                    <td data-label="Mode">{run.mode === "dry_run" ? "Test" : "Live"}</td>
                    <td data-label="Started"><time dateTime={run.started_at ?? run.created_at}>{formatWhen(run.started_at ?? run.created_at)}</time></td>
                    <td data-label="Duration">{duration(run.started_at, run.completed_at)}</td>
                    <td data-label="Progress">{run.steps_completed}/{run.steps_total}</td>
                    <td data-label="Est. model cost">{modelCost(run)}</td>
                    <td data-label="Status"><StatusBadge tone={runChipTone(run.status)}>{statusLabel(run.status)}</StatusBadge></td>
                    <td className="agents-run-row-action">
                      <button
                        className="btn"
                        type="button"
                        aria-label={`View ${run.mode === "dry_run" ? "test" : "live"} run for ${run.definition_name.replace(/^library:\s*/, "")} started ${formatWhen(run.started_at ?? run.created_at)}`}
                        onClick={() => onSelectRun(run.workflow_run_id)}
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
        {runsQuery.hasNextPage ? (
          <div className="agents-run-more">
            <button
              className="btn"
              type="button"
              disabled={runsQuery.isFetchingNextPage}
              onClick={() => void runsQuery.fetchNextPage()}
            >
              {runsQuery.isFetchingNextPage ? "Loading…" : "Load more"}
            </button>
          </div>
        ) : null}
        {runsQuery.isFetchNextPageError ? (
          <div className="agents-run-more-error" role="alert">
            More runs could not be loaded.
            <button className="btn" type="button" onClick={() => void runsQuery.fetchNextPage()}>Try again</button>
          </div>
        ) : null}
      </Surface>
    </>
  );
}
